// src/pages/Reports/EmiDueList.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Row,
  Col,
  Button,
  Typography,
  DatePicker,
  AutoComplete,
  Input,
  Table,
  message,
  Grid,
  Flex,
} from "antd";
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  PrinterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";
import dayjs from "dayjs";
import commaNumber from "comma-number";
import _ from "lodash";
import moment from "moment-timezone";

import ReportDom from "../../../components/common/ReportDom";
import {
  currentDateEndTime,
  currentDateStartTime,
  dateFormat,
} from "../../../lib/functions";
import { getDueEmisApi } from "../../../apiservice/accounts";
import { getAccountBySeachApi } from "../../../apiservice/setting";
import { exportEmiDueCsv } from "./component/exportEmiDueCsv";
import { useSelector } from "react-redux";

const { Title } = Typography;
const format = commaNumber.bindWith(",", ".");

const EmiDueList = ({ authInfo }) => {
  const navigate = useNavigate();
  const reportRef = useRef();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountQuery, setAccountQuery] = useState("");
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const { list, filters } = useSelector((state) => state.emiDue);
  const today = dayjs();
  const [byDate, setByDate] = useState({
    rawTo: today,
    dateTo: currentDateEndTime(today),
  });

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAccounts = async (query) => {
    setLoadingAccounts(true);
    try {
      let type = ["Customer"];
      if (authInfo?.subscription === "SuperERP" || authInfo?.branch_company_type === "Pharmacy") {
        type = ["Customer", "Patient"];
      }
      const res = await getAccountBySeachApi({
        query: query?.trim() || "",
        type: "debitor",
        acc_type_name: type,
      });
      setAccounts(res.data || []);
    } catch (err) {
      console.error("Error fetching accounts:", err);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const fetchReport = async () => {
    if (!selectedAccount) {
      message.warning("Please select a customer/debitor");
      return;
    }

    setLoading(true);
    try {
      const res = await getDueEmisApi({
        customerId: selectedAccount.acc_id,
        dateTo: byDate.dateTo,
      });
      setData(res.data || []);
    } catch (err) {
      console.error("Error fetching EMI Due List:", err);
      message.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: "EMI Due List",
    removeAfterPrint: true,
  });

  const handleNavigateToBack = () => {
    navigate("/reports");
  };
  const handleDownloadCsv = () => {
    exportEmiDueCsv({
      list,
      filters,
      title:    "EMI Due List",
      filename: "emi_due_list",
    });
  };

  const hasData = list.length > 0;
  const columns = [
    { title: "No.", render: (_, __, i) => i + 1 },
    { title: "EMI No", dataIndex: "emi_no" },
    { title: "Customer Name", dataIndex: "acc_name" },
    { title: "Contact", dataIndex: "contact_no" },
    {
      title: "From Date",
      dataIndex: "from_date",
      render: (v) => (v ? moment(v).format(dateFormat) : ""),
    },
    {
      title: "Last Date",
      dataIndex: "last_date",
      render: (v) => (v ? moment(v).format(dateFormat) : ""),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      align: "right",
      render: (v) => format(Number(v || 0).toFixed(2)),
    },
  ];

  return (
    <div className="p-4">
      {/* Header */}
      <Card
        className="shadow-md rounded-xl mb-4"
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleNavigateToBack}
              style={{ marginRight: 8 }}
            />
            <Title level={4} style={{ margin: 0 }}>
              EMI Due List
            </Title>
          </div>
        }
      extra={
            hasData && (
              <Flex gap={8}>
                <Button icon={<DownloadOutlined />} onClick={handleDownloadCsv}>
                  {!screens.xs && "CSV"}
                </Button>
                <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
                  {!screens.xs && "Print"}
                </Button>
              </Flex>
            )
          }
      />

      {/* Filters */}
      <Card className="shadow-md rounded-xl mb-4">
        <Row gutter={[16, 16]}>
          {/* Account Search */}
          <Col xs={24} sm={12} md={8}>
            <label>Search Customer/Debitor</label>
            <AutoComplete
              style={{ width: "100%" }}
              options={accounts.map((a) => ({
                label: a.acc_name,
                value: a.acc_name,
                account: a,
              }))}
              onSearch={fetchAccounts}
              onSelect={(_, option) => setSelectedAccount(option.account)}
              placeholder="Search Customer/Debitor"
              loading={loadingAccounts}
              allowClear
            >
              <Input />
            </AutoComplete>
          </Col>

          {/* Date */}
         <Col xs={24} sm={12} md={6}>
        <label>Date</label>
        <DatePicker
            value={byDate.rawTo}
            data-testid="emi-to-datepicker"
            format="DD-MMM-YYYY"   // 👈 This controls display: 01-Jan-2025
            onChange={(d) => {
            if (d) {
                const pureDate = d.endOf("day"); 
                setByDate({
                rawTo: pureDate,
                dateTo: currentDateEndTime(pureDate),
                });
            }
            }}
            style={{ width: "100%" }}
        />
        </Col>


          {/* Report Button */}
          <Col xs={24} sm={12} md={4} style={{ display: "flex", alignItems: "flex-end" }}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={fetchReport}
              loading={loading}
              block
            >
              Report
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Report Preview */}
      <Card className="shadow-md rounded-xl">
        <Table
          dataSource={data}
          rowKey="emi_no"
          columns={columns}
          pagination={false}
          bordered
          scroll={{ x: "max-content" }}
          size="small"
          summary={() =>
            data.length > 0 && (
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={6} align="right">
                  Grand Total
                </Table.Summary.Cell>
                <Table.Summary.Cell align="right">
                  {format(_.sumBy(data, (r) => Number(r.amount || 0)).toFixed(2))}
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )
          }
        />
      </Card>

      {/* Print Layout */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="EMI Due List"
          filters={{ account: selectedAccount, dateTo: byDate.dateTo }}
          renderTable={() => (
            <Table
              dataSource={data}
              rowKey="emi_no"
              columns={columns}
              pagination={false}
              bordered
              size="small"
            />
          )}
        />
      </div>
    </div>
  );
};

export default EmiDueList;
